"use client";

import { c as _c } from "react/compiler-runtime";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import useCreateModalState from "@/hooks/useCreateModalState";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Link from "next/link";
import React, { useMemo, useRef } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import type { VirtualItem } from "../../../../hooks/useVirtualizedWaves";
import { useVirtualizedWaves } from "../../../../hooks/useVirtualizedWaves";
import { useAuth } from "../../../auth/Auth";
import SectionHeader from "../waves/SectionHeader";
import WavesFilterToggle from "../waves/WavesFilterToggle";
import WebBrainLeftSidebarWave from "./WebBrainLeftSidebarWave";
import type { MinimalWave } from "@/contexts/wave/hooks/useEnhancedWavesListCore";
import { useSeizeSettingsOptional } from "@/contexts/SeizeSettingsContext";
function isValidWave(wave: unknown): wave is MinimalWave {
  if (wave === null || wave === undefined || typeof wave !== "object") {
    return false;
  }
  const w = wave as MinimalWave;
  return typeof w.id === "string" && w.id.length > 0 && typeof w.name === "string" && typeof w.isPinned === "boolean";
}
const EMPTY_WAVES_PLACEHOLDER_HEIGHT = "48px" as const;
const WAVE_ROW_HEIGHT_DEFAULT = 62 as const;
const WAVE_ROW_HEIGHT_COLLAPSED = 52 as const;
const PROFILE_FEED_TOOLTIP_ID = "profile-feed-shortcut-tooltip";
const PROFILE_FEED_LABEL = "Profile Waves Feed";
const TOOLTIP_STYLE = {
  padding: "6px 10px",
  background: "#37373E",
  color: "white",
  fontSize: "12px",
  fontWeight: 500,
  borderRadius: "6px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
  zIndex: 10000
} as const satisfies React.CSSProperties;
function MasonryGridIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="tw-size-4 tw-flex-shrink-0"><rect width="7" height="9" x="3" y="3" rx="1" /><rect width="7" height="5" x="14" y="3" rx="1" /><rect width="7" height="9" x="14" y="12" rx="1" /><rect width="7" height="5" x="3" y="16" rx="1" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
interface WebUnifiedWavesListWavesProps {
  readonly waves: MinimalWave[];
  readonly onHover: (waveId: string) => void;
  readonly hideHeaders?: boolean | undefined;
  readonly hideToggle?: boolean | undefined;
  readonly hidePin?: boolean | undefined;
  readonly scrollContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly basePath?: string | undefined;
  readonly isCollapsed?: boolean | undefined;
  readonly showProfileFeedShortcut?: boolean | undefined;
  readonly sentinelRef: React.RefObject<HTMLDivElement | null>;
}
function ProfileFeedAvatar(t0) {
  const $ = _c(9);
  const {
    isActive
  } = t0;
  const t1 = `tw-relative tw-size-8 tw-rounded-full tw-border tw-border-solid tw-transition tw-duration-200 desktop-hover:group-hover:tw-brightness-110 ${isActive ? "tw-border-primary-300/75 tw-opacity-100" : "tw-border-iron-700/80 tw-opacity-90 desktop-hover:group-hover:tw-border-iron-500/80 desktop-hover:group-hover:tw-opacity-100"}`;
  const t2 = `tw-h-full tw-w-full tw-rounded-full ${isActive ? "tw-bg-primary-500/90" : "tw-bg-iron-900"}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <div className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center ${isActive ? "tw-text-primary-50" : "tw-text-iron-400"}`;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MasonryGridIcon />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t4) {
    t6 = <div className={t4}>{t5}</div>;
    $[3] = t4;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t6) {
    t7 = <div className={t1}>{t3}{t6}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}
function isModifiedClick(event: React.MouseEvent<HTMLAnchorElement>) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1 || event.button === 2;
}
function WebProfileFeedShortcut(t0) {
  const $ = _c(24);
  const {
    basePath,
    isCollapsed
  } = t0;
  const {
    activeWave
  } = useMyStream();
  const isActive = activeWave.id === null;
  let t1;
  if ($[0] !== activeWave) {
    t1 = event => {
      if (event.defaultPrevented || isModifiedClick(event)) {
        return;
      }
      event.preventDefault();
      activeWave.set(null, {
        isDirectMessage: false
      });
    };
    $[0] = activeWave;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClick = t1;
  if (isCollapsed) {
    const t2 = `tw-group tw-flex tw-items-center tw-justify-center tw-py-2 tw-transition-all tw-duration-200 tw-ease-out ${isActive ? "tw-bg-iron-700/60 desktop-hover:hover:tw-bg-iron-700/70" : "desktop-hover:hover:tw-bg-iron-800/70"}`;
    const t3 = isActive ? "page" : undefined;
    let t4;
    if ($[2] !== isActive) {
      t4 = <ProfileFeedAvatar isActive={isActive} />;
      $[2] = isActive;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== basePath || $[5] !== handleClick || $[6] !== t3 || $[7] !== t4) {
      t5 = <Link href={basePath} prefetch={false} onClick={handleClick} aria-label={PROFILE_FEED_LABEL} aria-current={t3} className="tw-flex tw-items-center tw-justify-center tw-no-underline" data-tooltip-id={PROFILE_FEED_TOOLTIP_ID} data-tooltip-content={PROFILE_FEED_LABEL}>{t4}</Link>;
      $[4] = basePath;
      $[5] = handleClick;
      $[6] = t3;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t2 || $[10] !== t5) {
      t6 = <div className={t2}>{t5}</div>;
      $[9] = t2;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  const t2 = `tw-group tw-flex tw-items-center tw-gap-x-4 tw-px-5 tw-py-2 tw-transition-all tw-duration-200 tw-ease-out ${isActive ? "tw-bg-iron-700/60 desktop-hover:hover:tw-bg-iron-700/70" : "desktop-hover:hover:tw-bg-iron-800/80"}`;
  const t3 = isActive ? "page" : undefined;
  const t4 = `tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-space-x-3 tw-py-1 tw-no-underline tw-transition-all tw-duration-200 tw-ease-out ${isActive ? "tw-font-medium tw-text-white desktop-hover:group-hover:tw-text-white" : "tw-font-normal tw-text-iron-400 desktop-hover:group-hover:tw-text-iron-300"}`;
  let t5;
  if ($[12] !== isActive) {
    t5 = <ProfileFeedAvatar isActive={isActive} />;
    $[12] = isActive;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-min-w-0 tw-flex-1"><div className="tw-truncate tw-text-sm">{PROFILE_FEED_LABEL}</div></div>;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== basePath || $[16] !== handleClick || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t7 = <Link href={basePath} prefetch={false} onClick={handleClick} aria-current={t3} className={t4}>{t5}{t6}</Link>;
    $[15] = basePath;
    $[16] = handleClick;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t2 || $[22] !== t7) {
    t8 = <div className={t2}>{t7}</div>;
    $[21] = t2;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
const WebUnifiedWavesListWaves: React.FC<WebUnifiedWavesListWavesProps> = t0 => {
  const $ = _c(63);
  const {
    waves,
    onHover,
    hideHeaders: t1,
    hideToggle: t2,
    hidePin: t3,
    scrollContainerRef,
    basePath: t4,
    isCollapsed: t5,
    showProfileFeedShortcut: t6,
    sentinelRef
  } = t0;
  const hideHeaders = t1 === undefined ? false : t1;
  const hideToggle = t2 === undefined ? false : t2;
  const hidePin = t3 === undefined ? false : t3;
  const basePath = t4 === undefined ? "/waves" : t4;
  const isCollapsed = t5 === undefined ? false : t5;
  const showProfileFeedShortcut = t6 === undefined ? true : t6;
  const listContainerRef = useRef(null);
  const {
    connectedProfile
  } = useAuth();
  const {
    openWave,
    isApp
  } = useCreateModalState();
  const isTouchDevice = useIsTouchDevice();
  const seizeSettings = useSeizeSettingsOptional();
  const showCreateWaveButton = !isApp && !!connectedProfile;
  const shouldShowProfileFeedShortcut = !hideHeaders && showProfileFeedShortcut;
  let t7;
  if ($[0] !== seizeSettings || $[1] !== waves) {
    const announcements = [];
    const pinned = [];
    const regular = [];
    for (const wave of waves) {
      if (seizeSettings?.isAnnouncementsWave(wave.id)) {
        announcements.push(wave);
      } else {
        if (wave.isPinned) {
          pinned.push(wave);
        } else {
          regular.push(wave);
        }
      }
    }
    t7 = {
      announcementWaves: announcements,
      pinnedWaves: pinned,
      regularWaves: regular
    };
    $[0] = seizeSettings;
    $[1] = waves;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const {
    announcementWaves,
    pinnedWaves,
    regularWaves
  } = t7;
  const rowHeight = isCollapsed ? WAVE_ROW_HEIGHT_COLLAPSED : WAVE_ROW_HEIGHT_DEFAULT;
  const virtual = useVirtualizedWaves(regularWaves, "web-unified-waves-regular", scrollContainerRef ?? listContainerRef, listContainerRef, rowHeight, 5);
  let t8;
  if ($[3] !== hideHeaders || $[4] !== isCollapsed || $[5] !== openWave || $[6] !== showCreateWaveButton) {
    t8 = !hideHeaders && (isCollapsed ? showCreateWaveButton && <div className="tw-mb-3.5 tw-flex tw-justify-center tw-px-2"><div data-tooltip-id="create-wave-tooltip" data-tooltip-content="Create wave"><PrimaryButton onClicked={openWave} loading={false} disabled={false} padding="tw-p-2.5"><FontAwesomeIcon icon={faPlus} className="tw-size-4 tw-flex-shrink-0" /></PrimaryButton></div></div> : <SectionHeader label="Waves" rightContent={showCreateWaveButton ? <div data-tooltip-id="create-wave-tooltip" data-tooltip-content="Create wave"><PrimaryButton onClicked={openWave} loading={false} disabled={false} padding="tw-p-2.5"><FontAwesomeIcon icon={faPlus} className="tw-size-4 tw-flex-shrink-0" /></PrimaryButton></div> : undefined} />);
    $[3] = hideHeaders;
    $[4] = isCollapsed;
    $[5] = openWave;
    $[6] = showCreateWaveButton;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== hideHeaders || $[9] !== hideToggle || $[10] !== isCollapsed) {
    t9 = !hideHeaders && !hideToggle && !isCollapsed && <div className="tw-mt-4 tw-flex tw-px-4 tw-pb-3"><WavesFilterToggle /></div>;
    $[8] = hideHeaders;
    $[9] = hideToggle;
    $[10] = isCollapsed;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== basePath || $[13] !== isCollapsed || $[14] !== shouldShowProfileFeedShortcut) {
    t10 = shouldShowProfileFeedShortcut && <WebProfileFeedShortcut basePath={basePath} isCollapsed={isCollapsed} />;
    $[12] = basePath;
    $[13] = isCollapsed;
    $[14] = shouldShowProfileFeedShortcut;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== announcementWaves || $[17] !== basePath || $[18] !== hidePin || $[19] !== isCollapsed || $[20] !== onHover) {
    t11 = announcementWaves.length > 0 && <section className={`tw-flex tw-flex-col ${isCollapsed ? "tw-items-center tw-gap-y-2" : ""}`} aria-label="Announcement waves">{announcementWaves.filter(_temp).map(wave_1 => <div key={wave_1.id} className="tw-w-full"><WebBrainLeftSidebarWave wave={wave_1} onHover={onHover} showPin={!hidePin && !isCollapsed && wave_1.isPinned} basePath={basePath} collapsed={isCollapsed} /></div>)}</section>;
    $[16] = announcementWaves;
    $[17] = basePath;
    $[18] = hidePin;
    $[19] = isCollapsed;
    $[20] = onHover;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== announcementWaves.length || $[23] !== hideHeaders || $[24] !== pinnedWaves || $[25] !== regularWaves.length) {
    t12 = announcementWaves.length > 0 && !hideHeaders && (pinnedWaves.length > 0 || regularWaves.length > 0) && <div className="tw-my-2 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-700" />;
    $[22] = announcementWaves.length;
    $[23] = hideHeaders;
    $[24] = pinnedWaves;
    $[25] = regularWaves.length;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== basePath || $[28] !== hideHeaders || $[29] !== hidePin || $[30] !== isCollapsed || $[31] !== onHover || $[32] !== pinnedWaves) {
    t13 = !hideHeaders && pinnedWaves.length > 0 && <section className={`tw-flex tw-flex-col ${isCollapsed ? "tw-items-center tw-gap-y-2" : ""}`} aria-label="Pinned waves">{pinnedWaves.filter(_temp2).map(wave_3 => <div key={wave_3.id} className="tw-w-full"><WebBrainLeftSidebarWave wave={wave_3} onHover={onHover} showPin={!hidePin && !isCollapsed} basePath={basePath} collapsed={isCollapsed} /></div>)}</section>;
    $[27] = basePath;
    $[28] = hideHeaders;
    $[29] = hidePin;
    $[30] = isCollapsed;
    $[31] = onHover;
    $[32] = pinnedWaves;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== hideHeaders || $[35] !== pinnedWaves || $[36] !== regularWaves.length) {
    t14 = !hideHeaders && pinnedWaves.length > 0 && regularWaves.length > 0 && <div className="tw-my-2 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-700" />;
    $[34] = hideHeaders;
    $[35] = pinnedWaves;
    $[36] = regularWaves.length;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== basePath || $[39] !== hidePin || $[40] !== isCollapsed || $[41] !== onHover || $[42] !== regularWaves || $[43] !== sentinelRef || $[44] !== virtual) {
    t15 = regularWaves.length > 0 ? <section ref={listContainerRef} style={{
      height: virtual.totalHeight,
      position: "relative"
    }} aria-label="Regular waves list">{virtual.virtualItems.map(v => {
        if (v.index === regularWaves.length) {
          return <div key="sentinel" ref={sentinelRef} style={{
            position: "absolute",
            width: "100%",
            top: v.start,
            height: v.size
          }} />;
        }
        const wave_4 = regularWaves[v.index];
        if (!isValidWave(wave_4)) {
          console.warn("Invalid wave object at index", v.index, wave_4);
          return null;
        }
        return <div key={wave_4.id} style={{
          position: "absolute",
          width: "100%",
          top: v.start,
          height: v.size
        }}><WebBrainLeftSidebarWave wave={wave_4} onHover={onHover} showPin={!hidePin && !isCollapsed} basePath={basePath} collapsed={isCollapsed} /></div>;
      })}</section> : <div ref={listContainerRef} style={{
      minHeight: EMPTY_WAVES_PLACEHOLDER_HEIGHT
    }} />;
    $[38] = basePath;
    $[39] = hidePin;
    $[40] = isCollapsed;
    $[41] = onHover;
    $[42] = regularWaves;
    $[43] = sentinelRef;
    $[44] = virtual;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14 || $[50] !== t15) {
    t16 = <div>{t11}{t12}{t13}{t14}{t15}</div>;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t10 || $[53] !== t16 || $[54] !== t8 || $[55] !== t9) {
    t17 = <div className="tw-flex tw-flex-col">{t8}{t9}{t10}{t16}</div>;
    $[52] = t10;
    $[53] = t16;
    $[54] = t8;
    $[55] = t9;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== isTouchDevice || $[58] !== shouldShowProfileFeedShortcut) {
    t18 = !isTouchDevice && <><ReactTooltip id="create-wave-tooltip" place="bottom" offset={8} opacity={1} style={TOOLTIP_STYLE} border="1px solid #4C4C55" />{shouldShowProfileFeedShortcut && <ReactTooltip id={PROFILE_FEED_TOOLTIP_ID} place="right" offset={8} opacity={1} style={TOOLTIP_STYLE} border="1px solid #4C4C55" />}</>;
    $[57] = isTouchDevice;
    $[58] = shouldShowProfileFeedShortcut;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t17 || $[61] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[60] = t17;
    $[61] = t18;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  return t19;
};
WebUnifiedWavesListWaves.displayName = "WebUnifiedWavesListWaves";
export default WebUnifiedWavesListWaves;
function _temp(wave_0) {
  if (!isValidWave(wave_0)) {
    console.warn("Invalid announcement wave object", wave_0);
    return false;
  }
  return true;
}
function _temp2(wave_2) {
  if (!isValidWave(wave_2)) {
    console.warn("Invalid pinned wave object", wave_2);
    return false;
  }
  return true;
}